import { z } from 'zod';
import { type CustomPromptPostProcessingType, STPromptNodeSchema, STPromptOrderBlockSchema } from './base';
import { STPresetSchema, type STPreset } from './presets';

export interface ImportedSTGenerationSettings {
  temperature?: number;
  topP?: number;
  topK?: number;
  topA?: number;
  minP?: number;
  repetitionPenalty?: number;
  maxTokens?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
}

const STOpenAIPresetSchema = z
  .object({
    prompts: z.array(STPromptNodeSchema).default([]),
    prompt_order: z.array(STPromptOrderBlockSchema).default([]),
    use_sysprompt: z.boolean().optional(),
    custom_prompt_post_processing: z.string().optional(),
    world_info_depth: z.number().nullable().optional(),
    world_info_recursive: z.boolean().optional(),
    world_info_max_recursion_rounds: z.number().nullable().optional(),
    world_info: z
      .array(
        z.object({
          id: z.union([z.number(), z.string()]).optional(),
          name: z.string().optional(),
          content: z.string().optional(),
          key: z.array(z.string()).optional(),
          keysecondary: z.array(z.string()).optional(),
          selectiveLogic: z.number().optional(),
          caseSensitive: z.boolean().optional(),
          matchWholeWords: z.boolean().optional(),
          disable: z.boolean().optional(),
          position: z.union([z.number(), z.string()]).optional(),
          depth: z.number().optional(),
          order: z.number().optional(),
          insertion_order: z.number().optional(),
          role: z.union([z.number(), z.string()]).optional(),
          outletName: z.string().optional(),
          preventRecursion: z.boolean().optional(),
          excludeRecursion: z.boolean().optional(),
          delayUntilRecursion: z.boolean().optional(),
          scanDepth: z.number().nullable().optional(),
          sticky: z.number().optional(),
          cooldown: z.number().optional(),
          delay: z.number().optional(),
          characterFilter: z
            .object({
              isExclude: z.boolean().optional(),
              names: z.array(z.string()).optional(),
              tags: z.array(z.string()).optional(),
            })
            .optional(),
          useProbability: z.boolean().optional(),
          probability: z.number().optional(),
          triggers: z.array(z.string()).optional(),
          extensions: z.record(z.string(), z.unknown()).optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

/**
 * 从 SillyTavern OpenAI 预设提取可兼容字段
 */
export function importSTOpenAIPreset(raw: unknown, options: { enabled?: boolean } = {}): STPreset {
  const parsed = STOpenAIPresetSchema.parse(raw);
  const generationSettings = importSTGenerationSettings(raw);

  return STPresetSchema.parse({
    enabled: options.enabled ?? true,
    prompts: parsed.prompts,
    prompt_order: parsed.prompt_order,
    use_sysprompt: parsed.use_sysprompt ?? false,
    custom_prompt_post_processing: mapSTPostProcessing(parsed.custom_prompt_post_processing),
    temperature: generationSettings.temperature,
    topP: generationSettings.topP,
    topK: generationSettings.topK,
    topA: generationSettings.topA,
    minP: generationSettings.minP,
    repetitionPenalty: generationSettings.repetitionPenalty,
    maxTokens: generationSettings.maxTokens,
    presencePenalty: generationSettings.presencePenalty,
    frequencyPenalty: generationSettings.frequencyPenalty,
  });
}

export function importSTGenerationSettings(raw: unknown): ImportedSTGenerationSettings {
  const parsed = STOpenAIPresetSchema.parse(raw) as Record<string, unknown>;
  const result: ImportedSTGenerationSettings = {};

  const temperature = readOptionalNumber(parsed.temperature, { min: 0, max: 2 });
  if (temperature != null) {
    result.temperature = temperature;
  }

  const topP = readOptionalNumber(parsed.top_p, { min: 0, max: 1 });
  if (topP != null) {
    result.topP = topP;
  }

  const topK = readOptionalNumber(parsed.top_k, { min: 0, max: 500, integer: true });
  if (topK != null) {
    result.topK = topK;
  }

  const topA = readOptionalNumber(parsed.top_a, { min: 0, max: 1 });
  if (topA != null) {
    result.topA = topA;
  }

  const minP = readOptionalNumber(parsed.min_p, { min: 0, max: 1 });
  if (minP != null) {
    result.minP = minP;
  }

  const repetitionPenalty = readOptionalNumber(parsed.repetition_penalty, { min: 1, max: 2 });
  if (repetitionPenalty != null) {
    result.repetitionPenalty = repetitionPenalty;
  }

  const maxTokens = readOptionalNumber(parsed.openai_max_tokens ?? parsed.max_tokens ?? parsed.max_output_tokens, {
    min: 1,
    integer: true,
  });
  if (maxTokens != null) {
    result.maxTokens = maxTokens;
  }

  const presencePenalty = readOptionalNumber(parsed.presence_penalty, { min: -2, max: 2 });
  if (presencePenalty != null) {
    result.presencePenalty = presencePenalty;
  }

  const frequencyPenalty = readOptionalNumber(parsed.frequency_penalty, { min: -2, max: 2 });
  if (frequencyPenalty != null) {
    result.frequencyPenalty = frequencyPenalty;
  }

  return result;
}

function readOptionalNumber(
  value: unknown,
  options: {
    min?: number;
    max?: number;
    integer?: boolean;
  } = {},
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  let normalized = value;

  if (options.integer) {
    normalized = Math.round(normalized);
  }
  if (typeof options.min === 'number') {
    normalized = Math.max(options.min, normalized);
  }
  if (typeof options.max === 'number') {
    normalized = Math.min(options.max, normalized);
  }

  return normalized;
}

function mapSTPostProcessing(value: unknown): CustomPromptPostProcessingType {
  const normalized = typeof value === 'string' ? value.toLowerCase().trim() : '';
  if (normalized.startsWith('merge')) {
    return 'merge';
  }
  if (normalized.startsWith('semi')) {
    return 'semi';
  }
  if (normalized.startsWith('strict')) {
    return 'strict';
  }
  return '';
}
